import type { ApiEnvelope, ChatReply, DashboardSummary, Session } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "http://127.0.0.1:8787";

async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `HTTP_${response.status}`);
  }
  return (await response.json()) as T;
}

export async function getSession(signal?: AbortSignal): Promise<ApiEnvelope<Session>> {
  return readJson(
    await fetch(`${API_BASE}/v1/auth/session`, {
      credentials: "include",
      signal
    })
  );
}

export async function getDashboard(
  signal?: AbortSignal
): Promise<ApiEnvelope<DashboardSummary>> {
  return readJson(
    await fetch(`${API_BASE}/v1/dashboard/summary`, {
      credentials: "include",
      signal
    })
  );
}

export async function sendChat(message: string, signal?: AbortSignal): Promise<ChatReply> {
  return readJson(
    await fetch(`${API_BASE}/v1/chat`, {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({ message }),
      signal
    })
  );
}

// ---- UCP streaming ----

export type IntentPhase =
  | "RECEIVED"
  | "PLANNING"
  | "READY"
  | "RUNNING"
  | "VERIFYING"
  | "COMPLETED"
  | "WAITING_APPROVAL"
  | "PAUSED"
  | "HOLD"
  | "UNKNOWN"
  | "FAILED"
  | "CANCELLED";

export type IntentEventType =
  | "INTENT_RECEIVED"
  | "PLANNING_STARTED"
  | "PLAN_READY"
  | "RUNNING_STARTED"
  | "STEP_COMPLETED"
  | "WAITING_APPROVAL"
  | "PAUSED"
  | "RESUMED"
  | "CANCELLED"
  | "REVOKED"
  | "COMPLETED"
  | "FAILED"
  | "EVIDENCE_RECORDED";

export interface IntentEvent {
  event_id: string;
  intent_id: string;
  type: IntentEventType;
  at: string;
  trace_id: string;
  payload?: Record<string, unknown>;
}

export interface IntentProjectionLite {
  intent_id: string;
  phase: IntentPhase;
  verdict: "PASS" | "HOLD" | "UNKNOWN";
  scope: string;
  message: string;
  receipt?: {
    task_id: string;
    request_id: string;
    verdict: "PASS" | "HOLD" | "UNKNOWN";
    scope: string;
    passing_property: string;
    state_evidence_digest: string;
    generated_at: string;
    source: string;
  };
}

export interface CreateIntentResponse {
  intent_id: string;
  accepted_at: string;
  lane: number;
  invariants: Record<string, unknown>;
  projection: IntentProjectionLite;
}

function uuid(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return prefix + "-" + crypto.randomUUID().replace(/-/g, "").slice(0, 12);
  }
  return prefix + "-" + Math.random().toString(36).slice(2, 14);
}

export async function createIntent(
  message: string,
  signal?: AbortSignal
): Promise<CreateIntentResponse> {
  const intentId = uuid("int");
  const traceId = uuid("trace");
  const body = {
    intent_id: intentId,
    tenant_id: "default",
    actor_id: "chat_user",
    workspace_id: "default",
    goal: message,
    data_class: "internal",
    requested_capabilities: ["mouth.tts"],
    autonomy_mode: "bounded_auto",
    budget: { usd: 0.10, tokens: 10000 },
    deadline: new Date(Date.now() + 5 * 60 * 1000).toISOString(),
    idempotency_key: uuid("idem"),
    trace_id: traceId,
    created_at: new Date().toISOString()
  };
  return readJson(
    await fetch(`${API_BASE}/api/intents`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal
    })
  );
}

/**
 * Subscribe to the SSE event stream for an intent. The callback is
 * invoked for every event. Returns a close() function to cancel the stream.
 * Throws if EventSource is not available (the UI should fall back to /v1/chat).
 */
export function subscribeIntent(
  intentId: string,
  onEvent: (event: IntentEvent) => void,
  onError?: (err: Event) => void
): () => void {
  if (typeof EventSource === "undefined") {
    throw new Error("EVENTSOURCE_UNAVAILABLE");
  }
  const url = `${API_BASE}/api/intents/${encodeURIComponent(intentId)}/events`;
  const es = new EventSource(url, { withCredentials: true });
  es.onmessage = (e: MessageEvent) => {
    try {
      const parsed = JSON.parse(e.data) as IntentEvent;
      onEvent(parsed);
      if (parsed.type === "COMPLETED" || parsed.type === "FAILED" || parsed.type === "CANCELLED") {
        es.close();
      }
    } catch {
      // ignore malformed
    }
  };
  if (onError) es.onerror = onError;
  return () => es.close();
}

/**
 * Send an intent and stream every event to onEvent. Resolves when the
 * intent reaches a terminal state (COMPLETED, FAILED, CANCELLED) or the
 * caller cancels via signal. Throws if the initial POST fails.
 */
export async function sendIntentStreaming(
  message: string,
  onEvent: (event: IntentEvent) => void,
  signal?: AbortSignal
): Promise<{ intent_id: string; events: IntentEvent[] }> {
  const created = await createIntent(message, signal);
  const events: IntentEvent[] = [];
  await new Promise<void>((resolve) => {
    const close = subscribeIntent(
      created.intent_id,
      (e) => {
        events.push(e);
        onEvent(e);
        if (e.type === "COMPLETED" || e.type === "FAILED" || e.type === "CANCELLED") {
          close();
          resolve();
        }
      },
      () => {
        close();
        resolve();
      }
    );
    if (signal) {
      signal.addEventListener("abort", () => {
        close();
        resolve();
      });
    }
  });
  return { intent_id: created.intent_id, events };
}
